import { useEffect, useState, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import {
  Search,
  LayoutDashboard,
  CloudSun,
  Brain,
  Bell,
  ShieldAlert,
  Shirt,
  User,
  MapPin,
  ArrowRight,
  Map,
  Sun,
  GitCompare,
  Thermometer,
  Droplets,
  Wind,
  Eye,
  BellRing,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useLocation } from "@/components/location-provider"

interface CommandItem {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  description?: string
  action: () => void
  category: "navigation" | "action" | "location"
}

export function CommandPalette() {
  const navigate = useNavigate()
  const { locations, current, setCurrent } = useLocation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const commands: CommandItem[] = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, description: "Your weather overview", action: () => navigate("/"), category: "navigation" },
    { id: "forecast", label: "Forecast", icon: CloudSun, description: "Extended weather forecast", action: () => navigate("/forecast"), category: "navigation" },
    { id: "map", label: "Weather Map", icon: Map, description: "Interactive weather map layers", action: () => navigate("/map"), category: "navigation" },
    { id: "insights", label: "Insights", icon: Brain, description: "AI-powered weather analysis", action: () => navigate("/insights"), category: "navigation" },
    { id: "alerts", label: "Alerts", icon: Bell, description: "Active weather alerts", action: () => navigate("/alerts"), category: "navigation" },
    { id: "preparedness", label: "Preparedness", icon: ShieldAlert, description: "Emergency checklists", action: () => navigate("/preparedness"), category: "navigation" },
    { id: "clothing", label: "Clothing", icon: Shirt, description: "Outfit recommendations", action: () => navigate("/clothing"), category: "navigation" },
    { id: "compare", label: "Compare", icon: GitCompare, description: "Compare weather between cities", action: () => navigate("/compare"), category: "navigation" },
    { id: "astronomy", label: "Astronomy", icon: Sun, description: "Sun, moon, and golden hour data", action: () => navigate("/astronomy"), category: "navigation" },
    { id: "notifications", label: "Notifications", icon: BellRing, description: "Push alert settings", action: () => navigate("/notifications"), category: "navigation" },
    { id: "account", label: "Account", icon: User, description: "Settings & preferences", action: () => navigate("/account"), category: "navigation" },
    ...locations.map((loc) => ({
      id: `loc-${loc.key}`,
      label: `${loc.name}, ${loc.country}`,
      icon: MapPin,
      description: "Switch location",
      action: () => { setCurrent(loc); setOpen(false) },
      category: "location" as const,
    })),
  ]

  const filtered = query
    ? commands.filter(
        (c) =>
          c.label.toLowerCase().includes(query.toLowerCase()) ||
          c.description?.toLowerCase().includes(query.toLowerCase())
      )
    : commands

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((prev) => !prev)
        setQuery("")
        setActiveIndex(0)
      }
      if (e.key === "Escape") {
        setOpen(false)
      }
    }
    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown" || (e.ctrlKey && e.key === "n")) {
        e.preventDefault()
        setActiveIndex((prev) => (prev + 1) % filtered.length)
      }
      if (e.key === "ArrowUp" || (e.ctrlKey && e.key === "p")) {
        e.preventDefault()
        setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length)
      }
      if (e.key === "Enter" && filtered[activeIndex]) {
        e.preventDefault()
        filtered[activeIndex].action()
        setOpen(false)
      }
    },
    [filtered, activeIndex]
  )

  if (!open) return null

  const navItems = filtered.filter((c) => c.category === "navigation")
  const actionItems = filtered.filter((c) => c.category === "action")
  const locationItems = filtered.filter((c) => c.category === "location")

  return (
    <>
      <div
        className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <div className="fixed left-1/2 top-[20%] z-[101] w-full max-w-lg -translate-x-1/2">
        <div className="overflow-hidden rounded-xl border border-border/50 bg-popover/95 shadow-2xl shadow-black/20 backdrop-blur-xl">
          <div className="flex items-center gap-3 border-b border-border/30 px-4">
            <Search className="h-4 w-4 text-muted-foreground/60 flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search commands, pages, locations..."
              className="flex-1 bg-transparent py-3.5 text-sm outline-none placeholder:text-muted-foreground/40"
            />
            <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded-md border border-border/50 bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground/60">
              esc
            </kbd>
          </div>
          <div ref={listRef} className="max-h-80 overflow-y-auto p-2">
            {navItems.length > 0 && (
              <>
                <p className="px-2 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/40">
                  Navigation
                </p>
                {navItems.map((item, i) => {
                  const idx = filtered.indexOf(item)
                  return (
                    <button
                      key={item.id}
                      onClick={() => { item.action(); setOpen(false) }}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                        activeIndex === idx
                          ? "bg-primary/10 text-primary"
                          : "text-foreground/70 hover:bg-muted/50 hover:text-foreground"
                      )}
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      <span className="flex-1 font-medium">{item.label}</span>
                      {item.description && (
                        <span className="text-xs text-muted-foreground/50 hidden sm:block">{item.description}</span>
                      )}
                      <ArrowRight className={cn("h-3 w-3 flex-shrink-0 text-muted-foreground/30", activeIndex === idx && "text-primary")} />
                    </button>
                  )
                })}
              </>
            )}
            {locationItems.length > 0 && (
              <>
                <p className="px-2 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/40">
                  Locations
                </p>
                {locationItems.map((item, i) => {
                  const idx = filtered.indexOf(item)
                  return (
                    <button
                      key={item.id}
                      onClick={() => { item.action(); setOpen(false) }}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                        activeIndex === idx
                          ? "bg-primary/10 text-primary"
                          : "text-foreground/70 hover:bg-muted/50 hover:text-foreground"
                      )}
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      <span className="flex-1 font-medium">{item.label}</span>
                      <span className="text-xs text-muted-foreground/50 hidden sm:block">Switch to</span>
                    </button>
                  )
                })}
              </>
            )}
            {filtered.length === 0 && (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <Search className="h-8 w-8 text-muted-foreground/20" />
                <p className="text-sm text-muted-foreground/50">No results found</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 border-t border-border/30 px-4 py-2">
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground/40">
              <kbd className="rounded border border-border/30 px-1 py-0.5 font-mono text-[9px]">↑↓</kbd>
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground/40">
              <kbd className="rounded border border-border/30 px-1 py-0.5 font-mono text-[9px]">↵</kbd>
              <span>Select</span>
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground/40">
              <kbd className="rounded border border-border/30 px-1 py-0.5 font-mono text-[9px]">esc</kbd>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
